/**
 * Bike collection cards — color swatch image swap + TBS-style card title line.
 */
(function () {
  const COLOR_HINTS = [
    ["black", "#1a1a1a"],
    ["white", "#f5f5f5"],
    ["silver", "#b8bcc4"],
    ["grey", "#8a8f98"],
    ["gray", "#8a8f98"],
    ["red", "#c62828"],
    ["brick", "#9e3b2f"],
    ["blue", "#2563a8"],
    ["navy", "#1e3a5f"],
    ["green", "#2d6a3e"],
    ["olive", "#5c6b3c"],
    ["teal", "#0d7c7c"],
    ["orange", "#e07020"],
    ["yellow", "#e6c200"],
    ["purple", "#6b4c9a"],
    ["violet", "#6b4c9a"],
    ["pink", "#d65c8f"],
    ["brown", "#7a4f2e"],
    ["tan", "#c4a574"],
    ["beige", "#d9c9a8"],
    ["gold", "#c9a227"],
    ["fog", "#b8c4c8"],
    ["carbon", "#2b2b2b"],
  ];

  function colorFromName(name) {
    const n = String(name || "").toLowerCase();
    if (!n) return "";
    for (const [hint, hex] of COLOR_HINTS) {
      if (n.includes(hint)) return hex;
    }
    return "";
  }

  function applyDotColors(root) {
    root.querySelectorAll(".bike-card-swatches__btn").forEach((btn) => {
      const dot = btn.querySelector(".bike-card-swatches__dot");
      if (!dot || btn.querySelector(".bike-card-swatches__photo")) return;
      const cc1 = btn.getAttribute("data-color-code-1");
      const cc2 = btn.getAttribute("data-color-code-2");
      if (cc1 && cc2 && cc1 !== cc2) {
        dot.style.background = `linear-gradient(135deg, ${cc1}, ${cc2})`;
        return;
      }
      if (cc1) {
        dot.style.background = cc1;
        return;
      }
      const hex = colorFromName(btn.getAttribute("data-color-value"));
      if (hex) dot.style.background = hex;
    });
  }

  function slugify(text) {
    return String(text || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function cardRoot(el) {
    return el.closest("product-card") || el.closest(".product-card");
  }

  function cardNodes(root = document) {
    const seen = new Set();
    const out = [];
    root.querySelectorAll("product-card[data-product-id], product-card.product-card, .product-card").forEach((card) => {
      if (seen.has(card)) return;
      seen.add(card);
      out.push(card);
    });
    return out;
  }

  function normalizeImageUrl(src) {
    if (!src) return "";
    if (src.startsWith("//")) return `https:${src}`;
    return src;
  }

  function gallerySlides(card) {
    return [...(card?.querySelectorAll(".card-gallery slideshow-slide") || [])];
  }

  function primaryGalleryImg(card) {
    const visibleSlide = card?.querySelector('.card-gallery slideshow-slide[aria-hidden="false"]');
    if (visibleSlide) return visibleSlide.querySelector("img");
    return card?.querySelector(".card-gallery img, .product-media__image, [ref='cardGallery'] img");
  }

  function imageUrlsMatch(a, b) {
    if (!a || !b) return false;
    const na = normalizeImageUrl(a).split("?")[0];
    const nb = normalizeImageUrl(b).split("?")[0];
    return na === nb || na.endsWith(nb.split("/").pop()) || nb.endsWith(na.split("/").pop());
  }

  function setGalleryImage(card, src) {
    const url = normalizeImageUrl(src);
    if (!url || !card) return false;

    const slides = gallerySlides(card);
    const targetSlide = slides[0] || null;
    const img = targetSlide?.querySelector("img") || primaryGalleryImg(card);
    if (!img) return false;

    // Already showing this color — don't reload (avoids blank flash).
    if (
      imageUrlsMatch(img.currentSrc || img.src, url) ||
      imageUrlsMatch(img.getAttribute("src"), url)
    ) {
      return true;
    }

    const apply = () => {
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.removeAttribute("data_max_resolution");
      if (img.src !== url) img.src = url;
      if (targetSlide) {
        slides.forEach((slide, index) => {
          slide.setAttribute("aria-hidden", slide === targetSlide ? "false" : "true");
        });
      }
    };

    // Prefetch so the swap doesn't flash an empty frame.
    const pre = new Image();
    pre.decoding = "async";
    pre.onload = apply;
    pre.onerror = apply;
    pre.src = url;
    if (pre.complete) apply();

    return true;
  }

  function syncSwatchActiveState(card, activeBtn) {
    if (!card || !activeBtn) return;
    const value = activeBtn.getAttribute("data-color-value");
    card.querySelectorAll("[data-bike-card-swatches] .bike-card-swatches__btn").forEach((btn) => {
      const isActive =
        btn === activeBtn ||
        (value && btn.getAttribute("data-color-value") === value);
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  function activateSwatch(btn, wrap, card) {
    const src = btn.getAttribute("data-image-src");
    setGalleryImage(card, src);
    syncSwatchActiveState(card, btn);
    updateCardLinks(card, btn.getAttribute("data-variant-id"));
  }

  function applyBikeCardTitles() {
    document.querySelectorAll(".product-card[data-bike-card-line]").forEach((card) => {
      const line = card.getAttribute("data-bike-card-line");
      const vendor = card.getAttribute("data-vendor") || "";
      if (!line) return;
      const titleEl =
        card.querySelector('[class*="product-title"]') ||
        card.querySelector(".product-card__content h3") ||
        card.querySelector(".product-card__content a h3");
      if (!titleEl || titleEl.dataset.zcBikeTitleDone) return;
      titleEl.dataset.zcBikeTitleDone = "1";
      titleEl.innerHTML = "";
      if (vendor) {
        const v = document.createElement("span");
        v.className = "zc-bike-card-vendor";
        v.textContent = vendor;
        titleEl.appendChild(v);
      }
      const t = document.createElement("span");
      t.className = "zc-bike-card-line";
      t.textContent = line;
      titleEl.appendChild(t);
    });
  }

  function productLinks(card) {
    if (!card) return [];
    return [...card.querySelectorAll('a[href*="/products/"]')];
  }

  function updateCardLinks(card, variantId) {
    if (!variantId) return;
    productLinks(card).forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set("variant", String(variantId));
        link.href = url.toString();
      } catch (_e) {
        /* ignore malformed href */
      }
    });
  }

  function defaultInStockSwatch(wrap) {
    return (
      wrap.querySelector('.bike-card-swatches__btn[data-color-in-stock="true"]') ||
      wrap.querySelector(".bike-card-swatches__btn.is-active") ||
      wrap.querySelector(".bike-card-swatches__btn")
    );
  }

  function initDefaultInStockSwatchForCard(card) {
    if (!card || card.dataset.zcDefaultSwatchDone) return;
    const wrap =
      card.querySelector("[data-bike-card-swatches-bottom]:not([hidden])") ||
      card.querySelector("[data-bike-card-single-color]") ||
      card.querySelector("[data-bike-card-swatches]");
    if (!wrap) return;
    const btn = defaultInStockSwatch(wrap);
    if (!btn) return;
    card.dataset.zcDefaultSwatchDone = "1";
    // Sync active state + PDP links only — do not reload the featured photo on init/hover.
    syncSwatchActiveState(card, btn);
    updateCardLinks(card, btn.getAttribute("data-variant-id"));
  }

  function swatchCount(wrap) {
    return Number.parseInt(wrap.getAttribute("data-swatch-count") || "0", 10);
  }

  function wireSwatches(root) {
    applyDotColors(root);
    root.querySelectorAll("[data-bike-card-swatches]").forEach((wrap) => {
      if (wrap.dataset.zcSwatchesWired) return;
      wrap.dataset.zcSwatchesWired = "1";
      const card = cardRoot(wrap);
      if (!card) return;

      const count = swatchCount(wrap) || wrap.querySelectorAll(".bike-card-swatches__btn").length;
      if (count > 3) wrap.classList.add("bike-card-swatches--compact");

      wrap.querySelectorAll(".bike-card-swatches__btn").forEach((btn) => {
        if (btn.disabled) return;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          activateSwatch(btn, wrap, card);
        });
      });
    });
  }

  /** Insert after the full price block — not inside it (sale items have two price rows). */
  function swatchInsertAnchor(card) {
    const content = card.querySelector(".product-card__content");
    if (!content) return null;

    const priceBlocks = [...content.querySelectorAll("product-price, [class*='product-price']")];
    if (priceBlocks.length) return priceBlocks[priceBlocks.length - 1];

    const prices = [...content.querySelectorAll('[class*="price"]')];
    if (prices.length) {
      let node = prices[prices.length - 1];
      while (node.parentElement && node.parentElement !== content) {
        if (node.parentElement.parentElement === content) {
          node = node.parentElement;
          break;
        }
        node = node.parentElement;
      }
      return node;
    }

    return (
      card.querySelector('[class*="product-title"]')?.closest("div") ||
      card.querySelector(".product-card__content h3")?.parentElement
    );
  }

  function hasBikeSwatches(card) {
    return !!card?.querySelector("[data-bike-card-swatches]:not([data-bike-card-swatches-bottom])");
  }

  function ensureSwatchSlot(card) {
    if (!hasBikeSwatches(card)) return;
    if (card.querySelector("[data-bike-card-swatches-bottom], [data-bike-card-swatches-empty]")) return;
    const anchor = swatchInsertAnchor(card);
    if (!anchor?.parentNode) return;
    const empty = document.createElement("div");
    empty.className = "bike-card-swatches bike-card-swatches--empty";
    empty.setAttribute("data-bike-card-swatches-bottom", "");
    empty.setAttribute("data-bike-card-swatches-empty", "");
    empty.setAttribute("aria-hidden", "true");
    anchor.parentNode.insertBefore(empty, anchor.nextSibling);
  }

  function duplicateSwatchesBelowTitle(card) {
    const top = card.querySelector("[data-bike-card-swatches]:not([data-bike-card-swatches-bottom])");
    if (!top || card.querySelector("[data-bike-card-swatches-bottom]")) return false;
    const anchor = swatchInsertAnchor(card);
    if (!anchor?.parentNode) return false;
    const bottom = top.cloneNode(true);
    bottom.setAttribute("data-bike-card-swatches-bottom", "");
    delete bottom.dataset.zcSwatchesWired;
    anchor.parentNode.insertBefore(bottom, anchor.nextSibling);
    delete card.dataset.zcDefaultSwatchDone;
    wireSwatches(card);
    return true;
  }

  function buildModelFilterPills() {
    const host = document.querySelector("[data-bike-model-filter-host]");
    if (!host || host.dataset.zcModelBuilt) return;
    const models = new Map();
    document.querySelectorAll(".product-card[data-bike-model]").forEach((card) => {
      const slug = card.getAttribute("data-bike-model");
      const line = card.getAttribute("data-bike-card-line") || slug;
      if (slug) models.set(slug, line.split("|").slice(1).join("|").trim() || line);
    });
    if (!models.size) return;
    host.dataset.zcModelBuilt = "1";
    host.hidden = false;
    const wrap = document.createElement("div");
    wrap.className = "parts-subcats-wrap parts-subcats--sidebar";
    wrap.innerHTML = `<div class="parts-subcats"><details class="parts-subcats__facet" open><summary class="parts-subcats__facet-label">Model</summary><ul class="parts-subcats__grid parts-subcats__grid--model list-unstyled"></ul></details></div>`;
    const list = wrap.querySelector("ul");
    const all = document.createElement("li");
    all.className = "parts-subcats__item";
    all.innerHTML = `<button type="button" class="parts-subcats__pill parts-subcats__pill--active" data-bike-filter-group="model" data-bike-filter-value="" aria-pressed="true">All</button>`;
    list.appendChild(all);
    [...models.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .forEach(([slug, label]) => {
        const li = document.createElement("li");
        li.className = "parts-subcats__item";
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "parts-subcats__pill";
        btn.setAttribute("data-bike-filter-group", "model");
        btn.setAttribute("data-bike-filter-value", slug);
        btn.setAttribute("aria-pressed", "false");
        btn.textContent = label.length > 42 ? `${label.slice(0, 40)}…` : label;
        li.appendChild(btn);
        list.appendChild(li);
      });
    host.replaceWith(wrap);
    wrap.setAttribute("data-bike-model-filter-host", "");
  }

  function initCards(root = document) {
    applyBikeCardTitles();
    cardNodes(root).forEach((card) => {
      duplicateSwatchesBelowTitle(card);
      ensureSwatchSlot(card);
    });
    wireSwatches(root);
    cardNodes(root).forEach((card) => {
      initDefaultInStockSwatchForCard(card);
    });
    buildModelFilterPills();
  }

  let initFrame = null;
  function scheduleInit() {
    if (initFrame) cancelAnimationFrame(initFrame);
    initFrame = requestAnimationFrame(() => {
      initFrame = null;
      initCards(document);
    });
  }

  function observeProductGrid() {
    const selectors = [".product-grid", "results-list", "[data-product-grid]", ".zc-cat-products__track"];
    selectors.forEach((selector) => {
      const grid = document.querySelector(selector);
      if (!grid || grid.dataset.zcSwatchObserved) return;
      grid.dataset.zcSwatchObserved = "1";
      new MutationObserver(() => scheduleInit()).observe(grid, { childList: true, subtree: true });
    });
  }

  const REVEAL_SWATCH_SELECTOR = "[data-bike-card-swatches-bottom]:not([data-bike-card-swatches-empty])";

  /**
   * Top-layer swatch strip (Popover API).
   * The strip stays a DOM child of the card (so hover/focus bubbling is unchanged) but is
   * rendered in the browser top layer, pinned under the card with fixed viewport coords.
   * Nothing later in the page (footer, pagination, "Shop all") can paint over it and no
   * overflow:hidden ancestor (carousel track, grid item) can clip it.
   */
  const supportsSwatchPopover =
    typeof HTMLElement !== "undefined" &&
    "popover" in HTMLElement.prototype &&
    typeof HTMLElement.prototype.showPopover === "function";
  const openSwatchPopovers = new Set();
  let swatchViewportSyncBound = false;
  let swatchViewportSyncFrame = null;

  function swatchAnchorBox(swatch) {
    const card = cardRoot(swatch);
    return card?.querySelector(".product-card__content") || card || swatch.parentElement;
  }

  function positionSwatchPopover(swatch) {
    const anchor = swatchAnchorBox(swatch);
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = Number.parseFloat(getComputedStyle(document.documentElement).fontSize || "16") * 0.1;
    swatch.style.setProperty("top", `${Math.round((rect.bottom + gap) * 100) / 100}px`, "important");
    swatch.style.setProperty("left", `${Math.round(rect.left * 100) / 100}px`, "important");
    swatch.style.setProperty("width", `${Math.round(rect.width * 100) / 100}px`, "important");
  }

  function syncOpenSwatchPopovers() {
    if (swatchViewportSyncFrame || !openSwatchPopovers.size) return;
    swatchViewportSyncFrame = requestAnimationFrame(() => {
      swatchViewportSyncFrame = null;
      openSwatchPopovers.forEach((swatch) => {
        if (!swatch.isConnected) {
          openSwatchPopovers.delete(swatch);
          return;
        }
        positionSwatchPopover(swatch);
      });
    });
  }

  function bindSwatchViewportSync() {
    if (swatchViewportSyncBound) return;
    swatchViewportSyncBound = true;
    // capture: true also catches scrolling containers (carousel tracks), not just the window.
    window.addEventListener("scroll", syncOpenSwatchPopovers, { passive: true, capture: true });
    window.addEventListener("resize", syncOpenSwatchPopovers, { passive: true });
  }

  function openSwatchPopover(item) {
    if (!supportsSwatchPopover) return;
    const swatch = item.querySelector(REVEAL_SWATCH_SELECTOR);
    if (!swatch) return;
    if (swatch.getAttribute("popover") !== "manual") swatch.setAttribute("popover", "manual");
    positionSwatchPopover(swatch);
    if (!swatch.matches(":popover-open")) {
      try {
        swatch.showPopover();
      } catch (_e) {
        return;
      }
    }
    openSwatchPopovers.add(swatch);
    bindSwatchViewportSync();
  }

  function closeSwatchPopover(item) {
    if (!supportsSwatchPopover) return;
    item.querySelectorAll("[data-bike-card-swatches-bottom][popover]").forEach((swatch) => {
      openSwatchPopovers.delete(swatch);
      if (!swatch.matches(":popover-open")) return;
      try {
        swatch.hidePopover();
      } catch (_e) {
        /* already hidden / detached */
      }
    });
  }

  function revealItem(item) {
    item.classList.add("is-swatch-reveal");
    openSwatchPopover(item);
  }

  function unrevealItem(item) {
    item.classList.remove("is-swatch-reveal");
    closeSwatchPopover(item);
  }

  /** Hide swatches until hover/focus — site-wide (same as homepage category carousel). */
  function bindSwatchReveal() {
    if (document.documentElement.dataset.zcSwatchRevealBound === "true") return;
    document.documentElement.dataset.zcSwatchRevealBound = "true";

    if (!window.matchMedia("(hover: hover)").matches) return;

    function itemFrom(target) {
      if (!target?.closest) return null;
      return (
        target.closest(".product-grid__item") ||
        target.closest("product-card.product-card") ||
        target.closest(".product-card")
      );
    }

    document.addEventListener("mouseover", (e) => {
      const item = itemFrom(e.target);
      if (!item) return;
      // Don't short-circuit on the class alone: section scripts (homepage carousel) may add
      // is-swatch-reveal before this handler runs, and the top-layer strip still has to open.
      // revealItem is idempotent.
      if (!item.querySelector(REVEAL_SWATCH_SELECTOR)) return;
      revealItem(item);
    });

    document.addEventListener("mouseout", (e) => {
      const item = itemFrom(e.target);
      if (!item) return;
      const related = e.relatedTarget;
      if (related && item.contains(related)) return;
      unrevealItem(item);
    });

    document.addEventListener("focusin", (e) => {
      const item = itemFrom(e.target);
      if (!item) return;
      if (!item.querySelector(REVEAL_SWATCH_SELECTOR)) return;
      revealItem(item);
    });

    document.addEventListener("focusout", (e) => {
      const item = itemFrom(e.target);
      if (!item) return;
      const related = e.relatedTarget;
      if (related && item.contains(related)) return;
      unrevealItem(item);
    });

    // Page hidden (tab switch, quick-add dialog, drawer) — don't leave a strip floating.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "hidden") return;
      document.querySelectorAll(".is-swatch-reveal").forEach(unrevealItem);
    });
  }

  function boot() {
    bindSwatchReveal();
    initCards(document);
    observeProductGrid();
    [100, 350, 900, 1800].forEach((ms) => window.setTimeout(() => initCards(document), ms));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("zc:collection-filters", scheduleInit);
  document.addEventListener("shopify:section:load", scheduleInit);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) scheduleInit();
  });

  window.zcBikeCardEnhancements = {
    applyBikeCardTitles,
    wireSwatches,
    slugify,
    buildModelFilterPills,
    refresh: scheduleInit,
  };
})();
